<template>
    <div class="bg-white h-full relative">
        <a :as="hasAgency ? 'a' : Link" prefetch="hover" :href="url" class="overflow-hidden rounded-sm item body-text"
            :class="{ loading: loading }">
            <skeleton style="aspect-ratio: 4/3" class="!h-[10rem] md:!h-[12rem] lg:!h-[13rem] !rounded-none"
                v-if="loading" />
            <div v-else class="relative overflow-hidden">
                <div class="!w-full overflow-hidden item-image" style="aspect-ratio: 4/3;">
                    <lazy-image :eager="priority">
                        <template #image="{ load }">
                            <img class="!w-full !h-full !object-cover" :src="(
                                tour.images?.[tour.cover_photo] ??
                                tour.images?.[0]
                            )?.medium
                                " :alt="tour.name" :loading="priority ? 'eager' : 'lazy'"
                                :fetchpriority="priority ? 'high' : undefined" decoding="async" @load.once="load" />
                        </template>
                    </lazy-image>
                </div>
                <div class="absolute top-0 bottom-0 left-0 right-0 flex justify-end items-start p-1 item-overlay">
                    <overlay-default :tour="tour" />
                </div>
                <promotions :tour="tour" />
            </div>

            <div ref="contentContainer" class="flex flex-col gap-2 p-2 justify-between">
                <div class="flex flex-col gap-2">
                    <div class="flex flex-col gap-1" v-if="loading">
                        <skeleton />
                        <skeleton width="5rem" />
                    </div>
                    <div v-else>
                        <h4 class="h6 !font-semibold body-text m-0" style="line-height: 1.2em">
                            {{ tour.name }}
                        </h4>
                        <rating v-if="tour.average_rating" :rating="tour.average_rating" />
                    </div>


                    <price v-if="!loading" :tour="tour" size="grid" />
                    <skeleton v-if="loading" />


                    <div v-else-if="tour.inclusions?.length > 0" class="flex flex-wrap gap-2 text-xs font-medium">
                        <div>Includes:</div>
                        <div class="flex items-center justify-center gap-1" v-for="tag in tour.inclusions">
                            <span :class="'fa ' + tag.icon" />
                            <span>{{ tag.name }}</span>
                        </div>
                    </div>
                </div>

                <div v-if="!loading && !hideCta" class="justify-end hidden lg:flex">
                    <p-button class="rounded-0" text severity="secondary">
                        <span style="letter-spacing: 0.1em" class="text-xs text-primary uppercase">
                            Explore
                        </span>
                        <span style="font-size: 8px" class="pi pi-chevron-right text-primary"></span>
                    </p-button>
                </div>
            </div>
        </a>
    </div>
</template>

<script setup lang="ts">
import { Tour } from "@/types";
import { Skeleton, Button as PButton } from "primevue";
import LazyImage from "@/Components/LazyImage.vue";
import { Link } from "@inertiajs/vue3";
import OverlayDefault from "../OverlayDefault.vue";
import Promotions from "../Promotions.vue";
import Price from "../Price.vue";
import Rating from "@/Components/Reviews/Rating.vue";
import { adjuster } from "@/utils/helpers";
import { onMounted, ref, watch } from "vue";
import useAgency from "@/Hook/useAgency";


const height = defineModel<number>('height')

const contentContainer = ref()

const emit = defineEmits(['adjustHeight'])


const { hasAgency } = useAgency()

const { tour } = defineProps<{
    tour: Tour;
    url?: string;
    loading?: boolean;
    height?: string;
    hideCta?: boolean;
    // Above-the-fold card: render its image eagerly (in SSR, high priority) so
    // it can serve as the Largest Contentful Paint element.
    priority?: boolean;
}>();

onMounted(function () {
    adjuster(height, contentContainer.value)
    window.addEventListener('resize', () => adjuster(height, contentContainer.value))
    window.addEventListener('load', () => adjuster(height, contentContainer.value))
    window.addEventListener('scroll', () => adjuster(height, contentContainer.value))
    window.addEventListener('focus', () => adjuster(height, contentContainer.value))
});


watch(height, () => adjuster(height, contentContainer.value))
</script>

<style lang="scss" scoped>
.item {
    .item-image {
        transform: scale(1);
        transition: 0.25s;
    }

    &.loading {
        cursor: wait !important;
    }

    &:hover {
        transition: 0.1s;
        cursor: pointer;

        .item-overlay {
            background-color: rgba(227, 227, 227, 0.205);
            transition: 0.25s;
        }

        .item-image {
            transform: scale(1.2);
            transition: 0.5s;
        }
    }
}
</style>
